// apps/web/src/features/agent/useAgentChat.ts
//
// Conversation state owner for the analyst agent page (#1876). Drives the
// Bearer SSE consumer (`streamAgentAsk`) and reduces the typed event stream
// into render-ready state: turns, the in-progress streamed answer, step
// progress, accumulated artifacts (→ right pane), the reconciliation verdict,
// and a terminal error.
//
// WHY (no-useEffect-route-data exemption): the stream is user-triggered by a
// composer submit, not route load — so it is exempt from the web module's
// no-`useEffect`-for-route-data rule. The only effect here is the unmount
// cleanup that aborts an in-flight stream.
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AgentRenderableArtifact,
  AgentV2AskRequest,
  AgentV2ConversationDetail,
  AgentV2Event,
  AgentVerdict,
} from '@magimp/shared';
import { ApiClientError } from '../../api/errors.js';
import { streamAgentAsk } from '../../api/agent/index.js';

export type AgentChatRole = 'user' | 'assistant';

export interface AgentTurn {
  id: string;
  role: AgentChatRole;
  text: string;
}

export interface AgentStep {
  label: string;
  status: 'start' | 'done';
}

export interface AgentChatError {
  code: string;
  message: string;
  // Diagnostic failure phase from the SSE error event (#2005). Absent for
  // transport/runtime failures thrown before any event is parsed. Surfaced as
  // a non-user-facing `data-stage` attribute, never as user copy.
  stage?: string;
}

export interface UseAgentChatResult {
  conversationId: string | undefined;
  turns: AgentTurn[];
  streaming: boolean;
  step: AgentStep | null;
  artifacts: AgentRenderableArtifact[];
  verdict: AgentVerdict | null;
  error: AgentChatError | null;
  submit: (question: string) => void;
  loadConversation: (conversation: AgentV2ConversationDetail) => void;
  newConversation: () => void;
}

export interface UseAgentChatOptions {
  onConversationIdChange?: (conversationId: string | undefined) => void;
}

let turnSeq = 0;
function nextTurnId(): string {
  turnSeq += 1;
  return `turn-${turnSeq}`;
}

export function useAgentChat(options: UseAgentChatOptions = {}): UseAgentChatResult {
  const { onConversationIdChange } = options;
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [step, setStep] = useState<AgentStep | null>(null);
  const [artifacts, setArtifacts] = useState<AgentRenderableArtifact[]>([]);
  const [verdict, setVerdict] = useState<AgentVerdict | null>(null);
  const [error, setError] = useState<AgentChatError | null>(null);

  const conversationIdRef = useRef<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  const abortInFlight = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const setActiveConversationId = useCallback(
    (id: string | undefined) => {
      conversationIdRef.current = id;
      setConversationId(id);
      onConversationIdChange?.(id);
    },
    [onConversationIdChange],
  );

  const applyEvent = useCallback(
    (event: AgentV2Event, assistantTurnId: string) => {
      switch (event.type) {
        case 'conversation_loaded':
          setActiveConversationId(event.conversationId);
          break;
        case 'thinking':
          setStep({ label: event.label, status: 'start' });
          break;
        case 'tool_call_started':
          setStep({ label: event.call.tool, status: 'start' });
          break;
        case 'tool_call_result':
          setStep({ label: event.result.tool, status: 'done' });
          break;
        case 'artifact_created':
          setArtifacts((prev) => [...prev, event.artifact]);
          break;
        case 'clarification_requested':
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === assistantTurnId ? { ...turn, text: event.question } : turn,
            ),
          );
          break;
        case 'glossary_proposal_created':
        case 'memory_proposal_created':
          break;
        case 'final':
          setActiveConversationId(event.conversationId);
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === assistantTurnId ? { ...turn, text: event.text } : turn,
            ),
          );
          break;
        case 'error':
          setError({ code: event.code, message: event.message });
          break;
      }
    },
    [setActiveConversationId],
  );

  const runStream = useCallback(
    async (body: AgentV2AskRequest, controller: AbortController, assistantTurnId: string) => {
      try {
        for await (const event of streamAgentAsk(body, controller.signal)) {
          if (controller.signal.aborted) return;
          applyEvent(event, assistantTurnId);
          if (
            event.type === 'final' ||
            event.type === 'error' ||
            event.type === 'clarification_requested'
          )
            break;
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(toChatError(e));
      } finally {
        // Only clear streaming state if this run is still the active one — a
        // newConversation()/unmount abort already reset state and may have
        // started nothing new.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setStreaming(false);
          setStep(null);
        }
      }
    },
    [applyEvent],
  );

  const submit = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (trimmed.length === 0) return;

      // A new submit aborts any in-flight run (the new run takes ownership of
      // `streaming`); the old run's `runStream` finally is a no-op because
      // `abortRef.current` no longer points at its controller.
      abortInFlight();

      const assistantTurnId = nextTurnId();
      setTurns((prev) => [
        ...prev,
        { id: nextTurnId(), role: 'user', text: trimmed },
        { id: assistantTurnId, role: 'assistant', text: '' },
      ]);
      setStep(null);
      setVerdict(null);
      setError(null);
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const body: AgentV2AskRequest =
        conversationIdRef.current === undefined
          ? { question: trimmed }
          : { question: trimmed, conversationId: conversationIdRef.current };

      void runStream(body, controller, assistantTurnId);
    },
    [abortInFlight, runStream],
  );

  const loadConversation = useCallback(
    (conversation: AgentV2ConversationDetail) => {
      abortInFlight();
      setActiveConversationId(conversation.id);
      setTurns(
        conversation.messages.map((message) => ({
          id: message.id,
          role: message.role,
          text: message.text,
        })),
      );
      setArtifacts(conversation.messages.flatMap((message) => message.artifacts));
      setStep(null);
      setVerdict(null);
      setError(null);
      setStreaming(false);
    },
    [abortInFlight, setActiveConversationId],
  );

  const newConversation = useCallback(() => {
    abortInFlight();
    setActiveConversationId(undefined);
    setTurns([]);
    setArtifacts([]);
    setStep(null);
    setVerdict(null);
    setError(null);
    setStreaming(false);
  }, [abortInFlight, setActiveConversationId]);

  useEffect(() => () => abortInFlight(), [abortInFlight]);

  return {
    conversationId,
    turns,
    streaming,
    step,
    artifacts,
    verdict,
    error,
    submit,
    loadConversation,
    newConversation,
  };
}

// WHY: the stream surfaces typed `error` events as state (handled in
// applyEvent). A thrown ApiClientError (e.g. a non-2xx open) carries the
// domain code so the UI can branch on `AGENT_NOT_CONFIGURED` without
// matching free-text; other failures fall back to a generic transport code.
function toChatError(e: unknown): AgentChatError {
  if (e instanceof ApiClientError) return { code: e.code, message: e.message };
  if (e instanceof Error) return { code: 'AGENT_STREAM_FAILED', message: e.message };
  return { code: 'AGENT_STREAM_FAILED', message: 'Agent stream failed' };
}
